import { useState } from "react";
import { motion } from "framer-motion";
import Confetti from "react-confetti-boom";

/**
 * Live, contained preview of the web app's welcome-credits popup
 * (web-frontend/src/shared/ui/WelcomeCreditsDialog.tsx). Same copy fallbacks,
 * {credits} substitution, motion timings and confetti burst; buttons are inert.
 *
 * The admin app doesn't carry the web app's design tokens, so the resolved
 * web-app palette (web-frontend/src/index.css) is scoped onto the frame as CSS
 * variables. The web app follows the user's theme, so the frame offers both.
 */
const DEFAULT_TITLE = "You're all set — welcome to Gullivr!";
const DEFAULT_MESSAGE = "You've got {credits} free credits to generate flyers, itineraries, videos, and more — on us.";

type Theme = "dark" | "light";

const THEME_VARS: Record<Theme, Record<string, string>> = {
  light: {
    "--wp-surface": "255 255 255",
    "--wp-ink": "28 28 28",
    "--wp-ink-muted": "107 113 128",
    "--wp-line": "205 211 224",
    "--wp-line-strong": "182 190 205",
    "--wp-brand": "25 162 73",
    "--wp-brand-soft": "224 243 231",
  },
  dark: {
    "--wp-surface": "28 31 38",
    "--wp-ink": "244 247 237",
    "--wp-ink-muted": "156 163 175",
    "--wp-line": "66 71 78",
    "--wp-line-strong": "84 89 95",
    "--wp-brand": "42 184 94",
    "--wp-brand-soft": "16 51 31",
  },
};

function parseCredits(v: string | number | null | undefined): number | null {
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  if (v === null || v === undefined || !/^\d+$/.test(v.trim())) return null;
  return parseInt(v.trim(), 10);
}

function SparklesIcon() {
  return (
    <svg className="h-4 w-4 shrink-0 text-[rgb(var(--wp-brand))]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z" />
      <path d="M20 3v4" />
      <path d="M22 5h-4" />
      <path d="M4 17v2" />
      <path d="M5 18H3" />
    </svg>
  );
}

function StarIcon() {
  return (
    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
  );
}

const segCls = (on: boolean) =>
  `px-3 py-1 text-xs font-medium rounded-md ${on ? "bg-indigo-600 text-white" : "text-gray-400 hover:text-white"}`;

function PopupFrame({
  credits,
  title,
  message,
  campaignName,
  defaultTitle = DEFAULT_TITLE,
  defaultMessage = DEFAULT_MESSAGE,
  notice,
}: {
  credits: number | null;
  title: string;
  message: string;
  campaignName?: string | null;
  defaultTitle?: string;
  defaultMessage?: string;
  notice?: { primaryLabel: string } | null;
}) {
  // No amount is ever invented: when unset/unloaded the placeholder (and its trailing space) is dropped.
  const fill = (text: string) =>
    (credits == null ? text.replace(/\{credits\}\s*/g, "") : text.replace(/\{credits\}/g, credits.toLocaleString("en-IN"))).replace(/\{(campaign|name)\}/g, notice ? (campaignName?.trim() || "Diwali 2026") : "{$1}");
  const heading = fill(title.trim() || defaultTitle);
  const body = fill(message.trim() || defaultMessage);
  return (
    <motion.div
      className="absolute inset-0 flex items-center justify-center bg-black/70 p-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
    >
      <div className="pointer-events-none absolute inset-0 z-[1] overflow-hidden" aria-hidden>
        <Confetti
          mode="boom"
          x={0.5}
          y={0.35}
          particleCount={140}
          shapeSize={12}
          spreadDeg={360}
          launchSpeed={3.2}
          colors={["#16a34a", "#22c55e", "#facc15", "#f97316", "#38bdf8", "#f472b6"]}
        />
      </div>

      <motion.div
        className="relative z-[2] w-full max-w-md rounded-2xl border border-[rgb(var(--wp-line))] bg-[rgb(var(--wp-surface))] p-6 text-center shadow-[0_1px_3px_rgba(24,24,27,0.05),0_10px_30px_rgba(24,24,27,0.05)]"
        initial={{ opacity: 0, scale: 0.85, y: 16 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ type: "spring", stiffness: 260, damping: 22 }}
      >
        <motion.h2
          className="flex items-center justify-center gap-1.5 text-lg font-bold text-[rgb(var(--wp-ink))]"
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
        >
          <SparklesIcon /> {heading}
        </motion.h2>
        <motion.p
          className="mt-1.5 text-sm text-[rgb(var(--wp-ink-muted))]"
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
        >
          {body}
        </motion.p>

        {campaignName && (
          <motion.p
            className="mt-2 inline-block rounded-full bg-[rgb(var(--wp-brand-soft))] px-3 py-1 text-xs font-semibold text-[rgb(var(--wp-brand))]"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.28 }}
          >
            {campaignName}
          </motion.p>
        )}

        {!notice && <motion.p
          className="mt-3 text-xs font-medium text-[rgb(var(--wp-ink-muted))]"
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          You're currently on the Free plan.
        </motion.p>}

        <motion.div
          className="mt-6 flex flex-col gap-2"
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35 }}
        >
          <button
            type="button"
            tabIndex={-1}
            className="inline-flex h-10 w-full cursor-default items-center justify-center gap-2 rounded-xl bg-[rgb(var(--wp-brand))] px-4 text-sm font-semibold text-white"
          >
            {notice ? notice.primaryLabel : "Start creating"}
          </button>
          {!notice && <button
            type="button"
            tabIndex={-1}
            className="inline-flex h-10 w-full cursor-default items-center justify-center gap-1.5 rounded-xl border border-[rgb(var(--wp-line-strong))] bg-[rgb(var(--wp-surface))] px-4 text-sm font-semibold text-[rgb(var(--wp-ink))]"
          >
            <StarIcon />
            Upgrade plan
          </button>}
        </motion.div>
      </motion.div>
    </motion.div>
  );
}

export default function WelcomePopupPreview({
  sellerCredits,
  buyerCredits,
  title,
  message,
  campaignName,
  defaultTitle,
  defaultMessage,
  notice,
}: {
  sellerCredits: string | number | null | undefined;
  buyerCredits: string | number | null | undefined;
  title: string | null | undefined;
  message: string | null | undefined;
  campaignName?: string | null;
  defaultTitle?: string;
  defaultMessage?: string;
  /** When set, renders the campaign notice card (single Acknowledge/Claim button) instead of the welcome popup. */
  notice?: { primaryLabel: string } | null;
}) {
  const [role, setRole] = useState<"seller" | "buyer">("seller");
  const [theme, setTheme] = useState<Theme>("dark");
  const [replayKey, setReplayKey] = useState(0);
  const credits = parseCredits(role === "seller" ? sellerCredits : buyerCredits);

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        <span className="text-xs font-medium text-gray-400">Live preview (what users see)</span>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex bg-gray-800 border border-gray-700 rounded-lg p-0.5">
            <button type="button" onClick={() => setRole("seller")} className={segCls(role === "seller")}>Seller</button>
            <button type="button" onClick={() => setRole("buyer")} className={segCls(role === "buyer")}>Buyer</button>
          </div>
          <div className="flex bg-gray-800 border border-gray-700 rounded-lg p-0.5">
            <button type="button" onClick={() => setTheme("dark")} className={segCls(theme === "dark")}>Dark</button>
            <button type="button" onClick={() => setTheme("light")} className={segCls(theme === "light")}>Light</button>
          </div>
          <button
            type="button"
            onClick={() => setReplayKey((k) => k + 1)}
            className="px-3 py-1.5 rounded-lg text-xs font-medium text-gray-300 border border-gray-700 hover:bg-gray-800"
          >
            Replay animation
          </button>
        </div>
      </div>
      <div
        className="relative h-[460px] w-full overflow-hidden rounded-xl border border-gray-800 bg-gray-950 font-sans"
        style={THEME_VARS[theme] as React.CSSProperties}
      >
        <PopupFrame
          key={replayKey}
          credits={credits}
          title={title ?? ""}
          message={message ?? ""}
          campaignName={campaignName}
          defaultTitle={defaultTitle}
          defaultMessage={defaultMessage}
          notice={notice}
        />
      </div>
    </div>
  );
}
